import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Rocket } from "lucide-react";
import { api } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { FadeIn } from "@/components/Reveal";
import SEO from "@/components/SEO";

const PRODUCTS_SEO = {
  title: "Bitumen & Petrochemical Products — Catalyst Petrochem",
  description:
    "Explore certified bitumen grades (VG-10/30/40), PMB, CRMB, emulsions, and specialty petrochemicals conforming to MoRTH, NHAI, IS and ASTM standards.",
};

const CATEGORY_ORDER = ["All", "Bituminous Products", "Specialty Petrochemicals", "Industrial Materials", "Fuels & Others"];

export default function Products() {
  const [products, setProducts] = useState(null);
  const [category, setCategory] = useState("All");

  useEffect(() => {
    let attempts = 0;
    const load = () => {
      api
        .get("/products")
        .then((r) => setProducts(r.data))
        .catch(() => {
          attempts += 1;
          if (attempts < 4) setTimeout(load, 1200);
          else setProducts([]);
        });
    };
    load();
  }, []);

  if (!products)
    return (
      <main data-testid="products-page">
        <SEO {...PRODUCTS_SEO} path="/products" />
        <PageHeader
          index="02"
          tag="Our Products"
          title="Certified grades. Zero compromise."
          description="Every consignment complies with MoRTH, NHAI, IS and ASTM standards — from viscosity-graded paving bitumen to specialty petrochemicals and industrial rubber."
        />
        <section className="px-6 pt-10 pb-20 md:px-12 lg:px-20">
          <div className="grid gap-px border border-white/10 bg-white/10 sm:grid-cols-2 lg:grid-cols-3" data-testid="products-loading">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="h-56 animate-pulse bg-[#101010]" />
            ))}
          </div>
        </section>
      </main>
    );

  const categories = CATEGORY_ORDER.filter((c) => c === "All" || products.some((p) => p.category === c));
  const filtered = category === "All" ? products : products.filter((p) => p.category === category);

  return (
    <main data-testid="products-page">
      <SEO {...PRODUCTS_SEO} path="/products" />
      <PageHeader
        index="02"
        tag="Our Products"
        title="Certified grades. Zero compromise."
        description="Every consignment complies with MoRTH, NHAI, IS and ASTM standards — from viscosity-graded paving bitumen to specialty petrochemicals and industrial rubber."
      />

      <section className="px-6 pt-10 pb-16 md:px-12 md:pb-20 lg:px-20">
        <div className="mb-12 flex flex-wrap gap-3" data-testid="product-filters">
          {categories.map((c) => (
            <button
              key={c}
              onClick={() => setCategory(c)}
              data-testid={`filter-${c.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              className={`border px-5 py-2.5 font-mono text-[11px] uppercase tracking-[0.15em] transition-colors duration-300 ${
                category === c
                  ? "border-brand bg-brand text-white"
                  : "border-white/15 text-neutral-400 hover:border-brand hover:text-brand"
              }`}
            >
              {c}
            </button>
          ))}
        </div>

        <div className="grid gap-px border border-white/10 bg-white/10 sm:grid-cols-2 lg:grid-cols-3">
          {filtered.map((p, i) => (
            <FadeIn key={p.id} delay={i * 0.04} className="h-full">
              <Link
                to={`/products/${p.slug}`}
                data-testid={`product-card-${p.slug}`}
                className="group flex h-full flex-col bg-[#0A0A0A] p-8 transition-colors duration-300 hover:bg-[#141414]"
              >
                <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500">{p.category}</p>
                <h3 className="mt-4 font-display text-2xl font-medium uppercase tracking-normal text-white transition-colors duration-300 group-hover:text-brand">
                  {p.name}
                </h3>
                <p className="mt-3 line-clamp-3 text-sm leading-relaxed text-neutral-500">{p.description}</p>
                <div className="mt-auto flex items-center justify-between pt-8">
                  <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-brand">{p.standard}</span>
                  <ArrowUpRight className="h-4 w-4 text-neutral-600 transition-all duration-300 group-hover:translate-x-1 group-hover:text-brand" />
                </div>
              </Link>
            </FadeIn>
          ))}
          <FadeIn delay={filtered.length * 0.04} className="h-full sm:col-span-2">
            <div
              className="relative flex h-full min-h-56 flex-col items-center justify-center overflow-hidden border border-dashed border-brand/40 bg-[#0A0A0A] p-8 text-center"
              data-testid="products-stay-tuned"
            >
              <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_50%_120%,rgba(255,87,34,0.14),transparent_60%)]" />
              <span className="relative mb-6 flex h-12 w-12 items-center justify-center">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-brand/25" />
                <span className="absolute inline-flex h-8 w-8 animate-ping rounded-full bg-brand/20" style={{ animationDelay: "0.5s" }} />
                <Rocket className="relative h-6 w-6 text-brand" />
              </span>
              <p className="shimmer-text font-mono text-[11px] uppercase tracking-[0.35em]">Stay Tuned</p>
              <p className="mt-3 font-display text-2xl font-medium uppercase tracking-tight text-neutral-300">
                More products launching soon<span className="launch-dots" />
              </p>
              <p className="mt-2 max-w-xs text-xs leading-relaxed text-neutral-600">
                New grades and specialty petrochemicals are joining the range soon.
              </p>
            </div>
          </FadeIn>
        </div>
      </section>
    </main>
  );
}
